import { Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import dayjs from "dayjs";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import {
  generatePath,
  useMatch,
  useNavigate,
  useParams,
  Navigate,
} from "react-router-dom";
import {
  usePostAgreementAttachmentMutation,
  useDeleteAgreementAttachmentMutation,
  useDeleteAgreementMutation,
  useGetAgreementItemQuery,
  useGetPartnerComplexContractQuery,
  useGetPartnerAgreementTemplateEditQuery,
  usePatchAgreementMutation,
  useDownloadPartnerTemplateMutation,
} from "src/entities/partnerContracts/model/api/api";
import { getAgreementModalTitle } from "src/entities/partnerContracts/model/helpers/helpers";
import { useGetPartnerQuery } from "src/shared/api/partner";
import {
  ModalComponent,
  EConfirmModalType,
  Loader,
} from "src/shared/components";
import { ContractBody } from "src/shared/components/contracts/ContractBody";
import { modalText } from "src/shared/constants/form.constants";
import { routes } from "src/shared/constants/routes.constants";
import { showApiError } from "src/shared/helpers/api.helpers";
import { useConfirmModal } from "src/shared/hooks";
import { agreementDefaultFormValues } from "../../../model/constants/formValues";
import { IAgreementFormValues } from "../../../model/types";
import { AgreementDialogFooter } from "./AgreementDialogFooter";
import { AgreementForm } from "./AgreementForm";

const downloadBlobUrl = (url: string, filename: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export function EditPartnerAgreement() {
  const [isDeleting, setIsDeleting] = useState(false);
  const { partner_id, contract_id, agreement_id } = useParams();
  const navigate = useNavigate();
  const isIframeRoute = useMatch("/iframe/*");

  const goBackRoute = generatePath(
    isIframeRoute ? routes.iframePartnerContracts : routes.partnerContractsList,
    { partner_id },
  );

  const { data: partner } = useGetPartnerQuery(partner_id || skipToken);
  const [deleteAgreement, { isLoading: isLoadingDelete }] =
    useDeleteAgreementMutation();

  const [
    downloadPartnerTemplate,
    { isLoading: isLoadingDownloadPartnerTemplate },
  ] = useDownloadPartnerTemplateMutation();

  const deleteAgreementHandler = async () => {
    try {
      setIsDeleting(true);
      await deleteAgreement({
        agreement_id: String(agreement_id),
        contract_id: String(contract_id),
        partner_id: String(partner_id),
      }).unwrap();

      navigate(goBackRoute);
    } catch (e) {
      setIsDeleting(false);
      showApiError(e);
    }
  };

  const handleExport = async () => {
    if (!partner_id || !contract_id || !agreement_id) return;

    try {
      const url = await downloadPartnerTemplate({
        partner_id,
        contract_id,
        agreement_id,
      }).unwrap();

      downloadBlobUrl(
        url,
        `ДС-${partner?.name}-${dayjs(agreement?.agreement_date).format(
          "DD.MM.YYYY",
        )}.docx`,
      );
    } catch {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  };
  const { data: complexContract, error: complexContractError } =
    useGetPartnerComplexContractQuery(
      partner_id && contract_id && !isLoadingDelete
        ? {
            partner_id,
            contract_id,
          }
        : skipToken,
    );

  const {
    data: agreementTemplateText,
    isFetching: isFetchingAgreementTemplate,
  } = useGetPartnerAgreementTemplateEditQuery(
    partner_id && contract_id && agreement_id && !isDeleting
      ? {
          partner_id,
          contract_id,
          agreement_id,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const modalTitle =
    complexContract && partner
      ? getAgreementModalTitle({
          partner_name: partner.name,
          contract_number: complexContract.contract_number,
        })
      : "";

  const [patchAgreement, { isLoading: isLoadingPatch }] =
    usePatchAgreementMutation();

  const [postAttachment, { isLoading: isLoadingPostAttachment }] =
    usePostAgreementAttachmentMutation();
  const [deleteAttachment, { isLoading: isLoadingDeleteAttachment }] =
    useDeleteAgreementAttachmentMutation();

  const isLoading =
    isLoadingPatch || isLoadingPostAttachment || isLoadingDeleteAttachment;

  const methods = useForm<IAgreementFormValues>({
    mode: "onChange",
    defaultValues: agreementDefaultFormValues,
  });
  const {
    handleSubmit,
    reset,
    getFieldState,
    formState: { isDirty, isSubmitting },
  } = methods;
  const fetchData =
    agreement_id && contract_id && partner_id && !isSubmitting
      ? {
          agreement_id,
          contract_id,
          partner_id,
        }
      : skipToken;

  const {
    data: agreement,
    isFetching,
    error: agreementError,
  } = useGetAgreementItemQuery(fetchData);

  const handleSaveAgreement = async (formData: IAgreementFormValues) => {
    const { file, data } = formData;
    const promises = [];

    const hadAttachment = Boolean(agreement?.file_path);
    const withEditsNow = Boolean(data.is_agreement_with_edits);
    const isEditsDirty = getFieldState("data.is_agreement_with_edits").isDirty;

    if (isEditsDirty) {
      if (!withEditsNow && hadAttachment) {
        promises.push(
          deleteAttachment({
            agreement_id: String(agreement_id),
            partner_id: String(partner_id),
            contract_id: String(contract_id),
          }).unwrap(),
        );
      }
      if (withEditsNow && file) {
        const fd = new FormData();
        fd.append("file", file);
        promises.push(
          postAttachment({
            agreement_id: String(agreement_id),
            partner_id: String(partner_id),
            contract_id: String(contract_id),
            file: fd,
          }).unwrap(),
        );
      }
    } else {
      if (withEditsNow && file) {
        const fd = new FormData();
        fd.append("file", file);
        promises.push(
          postAttachment({
            agreement_id: String(agreement_id),
            partner_id: String(partner_id),
            contract_id: String(contract_id),
            file: fd,
          }).unwrap(),
        );
      }
    }

    if (getFieldState("data").isDirty) {
      promises.push(
        patchAgreement({
          ...data,
          contract_id: Number(contract_id),
          partner_id: Number(partner_id),
          agreement_id: Number(agreement_id),
          agreement_date: data.agreement_date.format("YYYY-MM-DD"),
        }).unwrap(),
      );
    }

    try {
      await Promise.all(promises);
      navigate(goBackRoute);
    } catch (e) {
      showApiError(e);
    }
  };
  const { modal, showModal } = useConfirmModal({
    onResolve: () => navigate(goBackRoute),
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: { isLoading: false, title: modalText.continue },
    children: <Typography>{modalText.unsavedChanges}</Typography>,
    title: modalText.confirmAction,
  });

  const attachmentUrl = agreement?.file_path || undefined;

  useEffect(() => {
    if (agreement) {
      reset({
        data: {
          agreement_date: dayjs(agreement.agreement_date),
          is_active: agreement.is_active,
          is_original_presented: agreement.is_original_presented,
          is_agreement_with_edits: agreement.is_agreement_with_edits,
          note: agreement.note,
        },
        file: null,
      });
    }
  }, [agreement, reset]);

  if (!partner_id || !contract_id || !agreement_id) {
    return <Navigate to="/404" replace />;
  }

  if (
    complexContractError &&
    "status" in complexContractError &&
    complexContractError.status === 404
  ) {
    return <Navigate to="/404" replace />;
  }

  if (
    agreementError &&
    "status" in agreementError &&
    agreementError.status === 404
  ) {
    return <Navigate to="/404" replace />;
  }

  return (
    <ModalComponent
      width={() => ({ xs: "100%", sm: "95vw", xxl: 1520 })}
      boxProps={{ sx: { height: "90vh" } }}
      disableBodyPadding
      initialBodyShadow
      title={modalTitle}
      onClose={isDirty ? showModal : () => navigate(goBackRoute)}
      open
      modalFooter={
        <AgreementDialogFooter
          isEdit
          isLoadingDelete={isLoadingDelete}
          isLoading={isLoading}
          isLoadingExport={isLoadingDownloadPartnerTemplate}
          onDelete={deleteAgreementHandler}
          onExport={handleExport}
          onSubmit={handleSubmit(handleSaveAgreement)}
          onCancel={isDirty ? showModal : () => navigate(goBackRoute)}
          attachmentUrl={attachmentUrl}
        />
      }
    >
      {(isFetching || isFetchingAgreementTemplate) && (
        <Loader blur position="absolute" />
      )}
      {modal}
      <FormProvider {...methods}>
        <ContractBody
          html={agreementTemplateText}
          pdfUrl={attachmentUrl}
          form={<AgreementForm attachmentUrl={attachmentUrl} />}
        />
      </FormProvider>
    </ModalComponent>
  );
}
